/**
 * AI Intelligent Studio — card icon registry and the cards that are not config-driven.
 *
 * A tenant's `screens[]` group names its artwork with an `icon` key; this maps that key to a
 * glyph + badge colour from the shared Figma icon set (`assets/svg/landing/kpiIcons.tsx`).
 * Follows the same shape as the PPA workflow `ICONS` registry (`utils/ppaHelpers.tsx`), which
 * resolves an API-supplied string the same way.
 *
 * Adding a module: give its `screens[]` group an `icon` and register a glyph + colour here. A
 * group with no `icon`, or one naming a key that is not here, keeps working — `StudioCardIcon`
 * renders a neutral tile instead.
 */

import { ComponentType } from 'react';

import {
    CoinsHandIcon,
    CubeIcon,
    PackageIcon,
    SaleIcon,
    TagIcon,
    TrendingUpIcon,
} from 'assets/svg/landing/kpiIcons';
import { StudioGroup } from 'types/landing';

export interface StudioIconDef {
    Icon: ComponentType<{ className?: string }>;
    bgClassName: string;
}

export const STUDIO_ICONS: Record<string, StudioIconDef> = {
    strategicPricing: { Icon: TagIcon, bgClassName: 'bg-landingYellowBg' },
    portfolioArchitecture: { Icon: PackageIcon, bgClassName: 'bg-landingPeacockBg' },
    tradePromotion: { Icon: SaleIcon, bgClassName: 'bg-landingBlueBg' },
    tradeArchitecture: { Icon: CoinsHandIcon, bgClassName: 'bg-validationErrorBg' },
    commercialPlanning: { Icon: TrendingUpIcon, bgClassName: 'bg-filterV2PrimaryBg' },
    dataStudio: { Icon: CubeIcon, bgClassName: 'bg-filterV2Border' },
};

/**
 * Cards the navigation config cannot produce yet. Appended after the config-driven groups and
 * rendered permanently muted with a disabled "View more". Kept as data so promoting one into
 * `screens[]` later is a delete here plus a config edit — not a component rewrite.
 */
export const STATIC_STUDIO_CARDS: StudioGroup[] = [
    {
        key: 'dataMlStudio',
        label: 'Data & ML Studio',
        description: 'Inspect econometric models and calibrate elasticities for analytics.',
        icon: 'dataStudio',
        columns: [],
        isDisabled: true,
    },
];

/** Cards per row. The detail panel is inserted after the row holding the open card. */
export const STUDIO_CARDS_PER_ROW = 3;
